import { Chip } from "@dropbox/dig-components/chip";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { LockLine, TeamLine } from "@dropbox/dig-icons/assets";
import { getTemplateIdFromString } from "@mirage/analytics/events/enums/template_id-utils";
import { PAP_Click_TemplateType } from "@mirage/analytics/events/types/click_template_type";
import { AccessType } from "@mirage/shared/compose/assist-api";
import {
  getTemplateDisplayName,
  PRE_CONFIGURED_TEMPLATE_IDS,
  templateIdToTemplateName,
} from "@mirage/shared/compose/assist-template";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { memo, useMemo } from "react";
import styles from "./ContextInputMessage.module.css";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { AIToolLibraryConfig } from "@mirage/mosaics/AIToolLibrary/hooks/useAIToolLibrary";
import type { AssistCustomTemplate } from "@mirage/shared/compose/assist-template";

interface TemplateChipsProps {
  currentTemplate: string | null;
  onTemplateChange: (templateID: string | null, templateName: string) => void;
  templates: AssistCustomTemplate[];
  didSelectContentType: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
  openLibrary: (config?: AIToolLibraryConfig) => void;
}
export const TemplateChips = memo(
  ({
    currentTemplate,
    onTemplateChange,
    templates,
    didSelectContentType,
    logComposeEvent,
    openLibrary,
  }: TemplateChipsProps) => {
    const teamTemplates = useMemo(
      () =>
        templates.filter((template) => template.accessType === AccessType.TEAM),
      [templates],
    );
    const individualTemplates = useMemo(
      () =>
        templates.filter(
          (template) => template.accessType === AccessType.INDIVIDUAL,
        ),
      [templates],
    );
    return (
      <>
        <Box display="flex" marginBottom="Micro Large">
          <Chip
            className={classnames(styles.ContextInputContentTypeMessageChip, {
              [styles.ContextInputContentTypeMessageChipSelected]:
                didSelectContentType && currentTemplate === undefined,
            })}
            key="other"
            variant="transparent"
            onClick={() => {
              onTemplateChange(null, i18n.t("assist_templates_type_scratch"));
              logComposeEvent(
                PAP_Click_TemplateType({
                  templateType: "start_from_scratch",
                  promptType: "user_entered",
                }),
              );
            }}
            isSelected={didSelectContentType && currentTemplate === undefined}
          >
            <Chip.Content>
              {i18n.t("assist_templates_type_scratch")}
            </Chip.Content>
          </Chip>
        </Box>

        {individualTemplates.length > 0 && (
          <Box marginBottom="Micro Large">
            <Text
              size="small"
              color="subtle"
              tagName="div"
              className={styles.ContextInputMessageInstruction}
            >
              {i18n.t("assist_templates_home_subtitle_your_templates")}
            </Text>
            {individualTemplates.map((option) => (
              <Chip
                className={classnames(
                  styles.ContextInputContentTypeMessageChip,
                  {
                    [styles.ContextInputContentTypeMessageChipSelected]:
                      option.id === currentTemplate,
                  },
                )}
                key={option.id}
                variant="transparent"
                onClick={() => {
                  onTemplateChange(option.id, option.name);
                  logComposeEvent(
                    PAP_Click_TemplateType({
                      templateType: "user_template",
                      templateId: "user_generated",
                      promptType: "user_entered",
                    }),
                  );
                }}
                isSelected={option.id === currentTemplate}
              >
                <Chip.IconAccessory>
                  <UIIcon src={LockLine} />
                </Chip.IconAccessory>
                <Chip.Content>{option.name}</Chip.Content>
              </Chip>
            ))}
          </Box>
        )}
        <Text
          size="small"
          color="subtle"
          tagName="div"
          className={styles.ContextInputMessageInstruction}
        >
          {i18n.t("assist_templates_home_subtitle_starter_templates")}
        </Text>
        <Box marginBottom="Micro Large">
          {PRE_CONFIGURED_TEMPLATE_IDS.slice(0, 7).map((option) => {
            const englishName = templateIdToTemplateName[option];
            const name = getTemplateDisplayName(englishName);
            return (
              <Chip
                className={classnames(
                  styles.ContextInputContentTypeMessageChip,
                  {
                    [styles.ContextInputContentTypeMessageChipSelected]:
                      option === currentTemplate,
                  },
                )}
                key={option}
                variant="transparent"
                onClick={() => {
                  onTemplateChange(option, name);
                  logComposeEvent(
                    PAP_Click_TemplateType({
                      templateType: "starter_template",
                      templateId: getTemplateIdFromString(option),
                      promptType: "user_entered",
                    }),
                  );
                }}
                isSelected={option === currentTemplate}
              >
                <Chip.Content>{name}</Chip.Content>
              </Chip>
            );
          })}
          <Chip
            className={styles.ContextInputContentTypeMessageChip}
            key="see_more"
            variant="transparent"
            onClick={() => {
              openLibrary({
                tab: "templates",
              });
            }}
          >
            <Chip.Content>
              {i18n.t("assist_templates_home_view_more")}
            </Chip.Content>
          </Chip>
        </Box>
        {teamTemplates.length > 0 && (
          <Box marginBottom="Micro Medium">
            <Text
              size="small"
              color="subtle"
              tagName="div"
              className={styles.ContextInputMessageInstruction}
            >
              {i18n.t("assist_templates_home_subtitle_company_templates")}
            </Text>
            {teamTemplates.map((option) => (
              <Chip
                className={classnames(
                  styles.ContextInputContentTypeMessageChip,
                  {
                    [styles.ContextInputContentTypeMessageChipSelected]:
                      option.id === currentTemplate,
                  },
                )}
                key={option.id}
                variant="transparent"
                onClick={() => {
                  onTemplateChange(option.id, option.name);
                  logComposeEvent(
                    PAP_Click_TemplateType({
                      templateType: "company_template",
                      templateId: "user_generated",
                      promptType: "user_entered",
                    }),
                  );
                }}
                isSelected={option.id === currentTemplate}
              >
                <Chip.IconAccessory>
                  <UIIcon src={TeamLine} />
                </Chip.IconAccessory>
                <Chip.Content>{option.name}</Chip.Content>
              </Chip>
            ))}
          </Box>
        )}
      </>
    );
  },
);
TemplateChips.displayName = "TemplateChips";
